import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom';
import ShoppingList from './components/ShoppingList';
import AllRecipes from './components/Recipes';
import More from './components/More';
import MealPlan from './components/MealPlan';
import NotFound from './components/NotFound';
import WeeklyRecipeDetailPage from './components/WeeklyRecipeDetailPage';
import { Toaster, toast } from 'sonner';
import { SplashScreen } from './components/SplashScreen';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PrivateRoute, PublicRoute, clearCachedProfile } from './components/PrivateRoute';
import { SignIn } from './components/SignIn';
import { SignUp } from './components/SignUp';
import { ForgotPassword } from './components/ForgotPassword';
import { OnboardingPlan } from './components/OnboardingPlan';
import { OnboardingStartDay } from './components/OnboardingStartDay';
import { OnboardingServings } from './components/OnboardingServings';
import { apiPatch, apiPost } from './utils/apiClient';
import { clearOnboardingInProgress, clearSignupFinishing, isOnboardingInProgress, markOnboardingInProgress } from './utils/signupFlow';
import { chooseTrialForOnboarding, startChosenTrial, startTrial } from './utils/planActions';
import { Plans } from './components/Plans';
import { CheckoutReturn } from './components/CheckoutReturn';
import { FamilySettings } from './components/FamilySettings';
import { AcceptInvitePage } from './components/AcceptInvitePage';
import RecipeDetailPage from './components/RecipeDetailPage';
import InvitationPage from './components/InvitationPage';
import HouseholdAccessEnded from './components/HouseholdAccessEnded';
import HouseholdAccessStartTrial from './components/HouseholdAccessStartTrial';
import TrialExpiredInterstitial from './components/TrialExpiredInterstitial';
import AccountDeletedPage from './components/AccountDeletedPage';
import { GlobalErrorBanner } from './components/GlobalErrorBanner';
import { installToastErrorRedirect } from './utils/errorDisplay';

//import { SpeedInsights } from "@vercel/speed-insights/react";

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [onboardingPrefs, setOnboardingPrefs] = useState({ weekStartDay: 'monday' });

  useEffect(() => {
    installToastErrorRedirect();
  }, []);
 
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <GlobalErrorBanner />
          {showSplash && (
            <SplashScreen onComplete={() => setShowSplash(false)} />
          )}
         {/* <Topnav /> */}
          <div className="content" >
            <Routes>
              <Route path="/" element={<Navigate to="/MealPlan" replace />} />

              <Route
                path="/MealPlan"
                element={
                  <PrivateRoute>
                    <MealPlanWrapper />
                  </PrivateRoute>
                }
              />
              <Route
                path="/MealPlan/Recipe/:weeklyPlanId"
                element={
                  <PrivateRoute>
                    <WeeklyRecipeDetailPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/ShoppingList"
                element={
                  <PrivateRoute>
                    <ShoppingList />
                  </PrivateRoute>
                }
              />
              <Route
                path="/More"
                element={
                  <PrivateRoute>
                    <More />
                  </PrivateRoute>
                }
              />
              <Route
                path="/AllRecipes"
                element={
                  <PrivateRoute>
                    <AllRecipesWrapper />
                  </PrivateRoute>
                }
              />

              <Route
                path="/recipe/:recipeId"
                element={
                  <PrivateRoute>
                    <RecipeDetailPage />
                  </PrivateRoute>
                }
              />

              <Route
                path="/plans"
                element={
                  <PrivateRoute>
                    <Plans />
                  </PrivateRoute>
                }
              />

              {/* Stripe Checkout returns here after payment (routes/billing.js RETURN_PATHS). */}
              <Route
                path="/plans/subscribed"
                element={
                  <PrivateRoute>
                    <CheckoutReturn />
                  </PrivateRoute>
                }
              />

              <Route
                path="/family"
                element={
                  <PrivateRoute>
                    <FamilySettings onBack={() => window.history.back()} />
                  </PrivateRoute>
                }
              />

              <Route
                path="/household-access-ended"
                element={
                  <PrivateRoute>
                    <HouseholdAccessEnded />
                  </PrivateRoute>
                }
              />

              <Route
                path="/trial-expired"
                element={
                  <PrivateRoute>
                    <TrialExpiredInterstitial />
                  </PrivateRoute>
                }
              />

              <Route path="/household-access/start-trial" element={<HouseholdAccessStartTrial />} />

              {/* After account deletion. Deliberately unguarded: the person is signed out. */}
              <Route path="/account-deleted" element={<AccountDeletedPage />} />

              <Route
                path="/accept-invite"
                element={<AcceptInvitePage />}
              />

              <Route
                path="/invite"
                element={<InvitationPage />}
              />

              <Route
                path="/onboarding/plan"
                element={
                  <PrivateRoute>
                    <OnboardingPlanPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/onboarding/start-day"
                element={
                  <PrivateRoute>
                    <OnboardingStartDayPage setOnboardingPrefs={setOnboardingPrefs} />
                  </PrivateRoute>
                }
              />
              <Route
                path="/onboarding/servings"
                element={
                  <PrivateRoute>
                    <OnboardingServingsPage onboardingPrefs={onboardingPrefs} />
                  </PrivateRoute>
                }
              />

              <Route
                path="/auth/signin"
                element={
                  <PublicRoute>
                    <SignInPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/auth/signup"
                element={
                  <PublicRoute>
                    <SignUpPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/signup"
                element={
                  <PublicRoute>
                    <SignUpPage />
                  </PublicRoute>
                }
              />
              <Route
                path="/auth/forgot"
                element={
                  <PublicRoute>
                    <ForgotPasswordPage />
                  </PublicRoute>
                }
              />

              <Route path="*" element={<NotFound />} />     
            </Routes>
           {/* <SpeedInsights /> */}
          </div>
         <Toaster position="top-center" richColors closeButton />
        </div>

      </Router>
    </AuthProvider>
    
  );
}

function MealPlanWrapper() {
  const { user } = useAuth();
  return <MealPlan userId={user?.uid || 'user-default'} />;
}

function AllRecipesWrapper() {
  const { user } = useAuth();
  return <AllRecipes userId={user?.uid || 'user-default'} />;
}

function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const pendingInvite = () => {
    try {
      const raw = localStorage.getItem('mp:pendingInvite');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  const nextParam = new URLSearchParams(location.search).get('next') || '';
  const nextPath = nextParam.startsWith('/') ? nextParam : '';
  return (
    <SignIn
      onSwitchToSignUp={() => navigate('/auth/signup')}
      onForgotPassword={() => navigate('/auth/forgot')}
      onSignInSuccess={() => {
        localStorage.setItem('mp:onboarded', '1');
        const pending = pendingInvite();
        if (pending?.search) {
          navigate(`/invite${pending.search}`, { replace: true });
        } else if (nextPath) {
          navigate(nextPath, { replace: true });
        } else {
          navigate('/MealPlan', { replace: true });
        }
      }}
    />
  );
}

function SignUpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const signupRef = new URLSearchParams(location.search).get('ref') || '';
  const pendingInvite = () => {
    try {
      const raw = localStorage.getItem('mp:pendingInvite');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };
  return (
    <SignUp
      signupRef={signupRef}
      onSwitchToSignIn={() => navigate('/auth/signin')}
      onSignUpSuccess={async () => {
        localStorage.removeItem('mp:onboarded');
        const pending = pendingInvite();
        // Each branch navigates away and only then releases PublicRoute's signed-in redirect,
        // so the meal planner never flashes up between sign-up and the next screen.
        if (pending?.search) {
          navigate(`/invite${pending.search}`, { replace: true });
        } else {
          try {
            await apiPost('/households/bootstrap', {});
            markOnboardingInProgress();
            navigate('/onboarding/plan', { replace: true });
          } catch (error) {
            console.error('Failed to bootstrap owner household:', error);
            toast.error('Could not finish setting up your household. Please try again.');
            // Same destination as before this fix, when PublicRoute had already redirected.
            navigate('/MealPlan', { replace: true });
          }
        }
        clearSignupFinishing();
      }}
    />
  );
}

function ForgotPasswordPage() {
  const navigate = useNavigate();
  return <ForgotPassword onBackToSignIn={() => navigate('/auth/signin')} />;
}

function OnboardingPlanPage() {
  const navigate = useNavigate();
  const inOnboarding = isOnboardingInProgress();
  // Fresh sign-up: record the choice and carry on; the trial starts when onboarding finishes.
  // Existing owner sent here with no plan: start the trial now and skip Week start / Servings,
  // which would overwrite the preferences they already have. Errors surface on the plan screen.
  const handleChooseTrial = async () => {
    if (inOnboarding) {
      chooseTrialForOnboarding();
      navigate('/onboarding/start-day');
      return;
    }
    await startTrial();
    clearCachedProfile();
    navigate('/MealPlan', { replace: true });
  };
  return <OnboardingPlan inOnboarding={inOnboarding} onChooseTrial={handleChooseTrial} />;
}

// Onboarding is finished by "Get Started" on Servings, the last screen. Only then does the
// chosen trial start, so someone who backs out part-way never starts one. Returns an error
// message (shown inline on the screen, never a toast) if the trial couldn't be started, so the
// user can try again; '' on success.
async function finishOnboarding(navigate) {
  try {
    await startChosenTrial();
  } catch (err) {
    console.warn('Failed to start trial at end of onboarding:', err);
    return "We couldn't start your free trial. Please try again.";
  }
  clearOnboardingInProgress();
  // The cached profile still says "no plan", which would bounce them straight back here.
  clearCachedProfile();
  navigate('/MealPlan', { replace: true });
  return '';
}

function OnboardingStartDayPage({ setOnboardingPrefs }) {
  const navigate = useNavigate();
  return (
    <OnboardingStartDay
      onContinue={({ weekStartDay }) => {
        const day = (weekStartDay || 'monday').toLowerCase();
        setOnboardingPrefs(prev => ({ ...prev, weekStartDay: day }));
        navigate('/onboarding/servings');
      }}
      // Skip this step only: keep the default (Monday) and carry on to Servings.
      onSkip={() => {
        setOnboardingPrefs(prev => ({ ...prev, weekStartDay: 'monday' }));
        navigate('/onboarding/servings');
      }}
      onBack={() => navigate('/onboarding/plan')}
    />
  );
}

function OnboardingServingsPage({ onboardingPrefs }) {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  return (
    <OnboardingServings
      error={error}
      submitting={submitting}
      onComplete={async ({ defaultServings }) => {
        setError('');
        setSubmitting(true);
        try {
          await apiPatch('/user/preferences', {
            weekStartDay: onboardingPrefs.weekStartDay || 'monday',
            defaultServes: defaultServings,
          });
        } catch (e) {
          console.warn('Failed to save onboarding preferences:', e);
          setError("We couldn't save your preferences. Please try again.");
          setSubmitting(false);
          return;
        }
        // On success this navigates to the meal planner, which is confirmation enough.
        const trialError = await finishOnboarding(navigate);
        if (trialError) {
          setError(trialError);
          setSubmitting(false);
        }
      }}
      onBack={() => navigate('/onboarding/start-day')}
    />
  );
}

export default App;